import { sql } from "drizzle-orm"
import type { AnyPgColumn } from "drizzle-orm/pg-core"
import {
  boolean,
  check,
  date,
  decimal,
  index,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uuid,
  varchar,
} from "drizzle-orm/pg-core"
import { liabilities } from "./asset"
import { companies } from "./company"
import { contacts, estateRoleEnum, locations, rolePositionEnum } from "./contact"
import { documents } from "./document"
import { estates } from "./estate"

// Enums

export const US_ONLY_TRUST_TYPE_VALUES = [
  "REVOCABLE_LIVING_TRUST",
  "IRREVOCABLE_TRUST",
  "ILIT",
  "CREDIT_SHELTER_TRUST",
  "QTIP_MARITAL_TRUST",
  "SLAT",
  "IDGT",
  "DYNASTY_TRUST",
  "CHARITABLE_LEAD_TRUST",
  "SPECIAL_NEEDS_TRUST",
  "DOMESTIC_ASSET_PROTECTION_TRUST",
] as const

export const CA_ONLY_TRUST_TYPE_VALUES = [
  "FAMILY_TRUST",
  "ALTER_EGO_TRUST",
  "JOINT_PARTNER_TRUST",
  "SPOUSAL_TRUST",
  "HENSON_TRUST",
  "GRADUATED_RATE_ESTATE",
  "BARE_TRUST",
] as const

export const SHARED_TRUST_TYPE_VALUES = [
  "CHARITABLE_REMAINDER_TRUST",
  "TESTAMENTARY_TRUST",
  "QUALIFIED_DISABILITY_TRUST",
] as const

export const trustTypeEnum = pgEnum("trust_type_enum", [
  ...US_ONLY_TRUST_TYPE_VALUES,
  ...CA_ONLY_TRUST_TYPE_VALUES,
  ...SHARED_TRUST_TYPE_VALUES,
])

export const trustJurisdictionEnum = pgEnum("trust_jurisdiction_enum", ["US", "CA"])

export const trustFundingEnum = pgEnum("trust_funding_enum", ["INTER_VIVOS", "TESTAMENTARY"])

export type TrustType = (typeof trustTypeEnum.enumValues)[number]
export type TrustJurisdiction = (typeof trustJurisdictionEnum.enumValues)[number]

// Core Tables

// Purpose: Stores information about a bond associated with a trust.
export const trustBonds = pgTable("trust_bond", {
  id: uuid("id").primaryKey().defaultRandom(),
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "set null" }),
  trustBondRequired: boolean("trust_bond_required").default(false),
  bondAmount: decimal("bond_amount", { precision: 18, scale: 2 }).notNull(),
  bondDetails: text("bond_details"),
  providerContactId: uuid("provider_contact_id").references(() => contacts.id, {
    onDelete: "set null",
  }),
  issueDate: date("issue_date", { mode: "date" }),
  expirationDate: date("expiration_date", { mode: "date" }),
  isDraft: boolean("is_draft").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required. This schema sets the timestamp on creation,
  // and application logic or a trigger should handle updates.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

export const trustContacts = pgTable("trust_contact", {
  id: uuid("id").primaryKey().defaultRandom(),
  trustId: uuid("trust_id")
    .notNull()
    .references(() => trusts.id, { onDelete: "cascade" }),
  contactId: uuid("contact_id")
    .notNull()
    .references(() => contacts.id, { onDelete: "cascade" }),
  role: estateRoleEnum("estate_role_enum"), // Optional: track their role in this trust
  trustOwnershipPercentage: decimal("trust_ownership_percentage", {
    precision: 5,
    scale: 2,
  }).default("0"),
  isDraft: boolean("is_draft").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

export const trustDocuments = pgTable("trust_document", {
  id: uuid("id").primaryKey().defaultRandom(),
  trustId: uuid("trust_id")
    .notNull()
    .references(() => trusts.id, { onDelete: "cascade" }),
  documentId: uuid("document_id")
    .notNull()
    .references(() => documents.id, { onDelete: "cascade" }),
  isDraft: boolean("is_draft").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Links companies to trusts (e.g., corporate trustees)
export const trustCompanies = pgTable("trust_company", {
  id: uuid("id").primaryKey().defaultRandom(),
  trustId: uuid("trust_id")
    .notNull()
    .references(() => trusts.id, { onDelete: "cascade" }),
  companyId: uuid("company_id")
    .notNull()
    .references(() => companies.id, { onDelete: "cascade" }),
  role: estateRoleEnum("estate_role_enum"),
  isDraft: boolean("is_draft").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Stores information about a bond associated with a will.
export const willBonds = pgTable("will_bond", {
  id: uuid("id").primaryKey().defaultRandom(),
  willBondRequired: boolean("will_bond_required").default(false),
  documentId: uuid("document_id").references(() => documents.id, { onDelete: "set null" }),
  bondAmount: decimal("bond_amount", { precision: 18, scale: 2 }).notNull(),
  bondDetails: text("bond_details"),
  providerContactId: uuid("provider_contact_id").references(() => contacts.id, {
    onDelete: "set null",
  }),
  issueDate: date("issue_date", { mode: "date" }),
  expirationDate: date("expiration_date", { mode: "date" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
  // a database trigger is required. This schema sets the timestamp on creation,
  // and application logic or a trigger should handle updates.
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
})

// Purpose: Represents a legal trust established as part of an estate plan.
export const trusts = pgTable(
  "trust",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    estateId: uuid("estate_id")
      .notNull()
      .references(() => estates.id, { onDelete: "cascade" }),
    trustType: trustTypeEnum("trust_type_enum").notNull(),
    jurisdiction: trustJurisdictionEnum("jurisdiction").notNull(),
    funding: trustFundingEnum("funding"),
    revocable: boolean("revocable"),
    selfSettled: boolean("self_settled"),
    spendthriftClause: boolean("spendthrift_clause").default(false).notNull(),
    settlorAge65Plus: boolean("settlor_age_65_plus"),
    name: varchar("name", { length: 255 }).notNull(),
    description: text("description"),
    estateRole: estateRoleEnum("estate_role_enum"),
    rolePosition: rolePositionEnum("role_position_enum"),
    dateCreated: date("date_created", { mode: "date" }),
    trustBondId: uuid("trust_bond_id").references(() => trustBonds.id, { onDelete: "set null" }),
    locationId: uuid("location_id").references(() => locations.id, { onDelete: "set null" }),
    taxIdentificationNumber: varchar("tax_identification_number", { length: 50 }),
    employerIdentificationNumber: varchar("employer_identification_number", { length: 50 }),
    trustLiabilityId: uuid("trust_liability_id").references(() => liabilities.id, {
      onDelete: "set null",
    }),
    trusteeContactId: uuid("trustee_contact_id").references(() => contacts.id, {
      onDelete: "set null",
    }),
    trusteeCompanyId: uuid("trustee_company_id").references((): AnyPgColumn => companies.id, {
      onDelete: "set null",
    }),
    trusteeTrustId: uuid("trustee_trust_id").references((): AnyPgColumn => trusts.id, {
      onDelete: "set null",
    }),
    isDraft: boolean("is_draft").default(false).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    // Note: For 'ON UPDATE CURRENT_TIMESTAMP' functionality in PostgreSQL,
    // a database trigger is required. This schema sets the timestamp on creation,
    // and application logic or a trigger should handle updates.
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    check(
      "trust_jurisdiction_trust_type_chk",
      sql`(
        (
          ${table.jurisdiction} = 'US'
          AND ${table.trustType} IN (
            'REVOCABLE_LIVING_TRUST',
            'IRREVOCABLE_TRUST',
            'ILIT',
            'CREDIT_SHELTER_TRUST',
            'QTIP_MARITAL_TRUST',
            'SLAT',
            'IDGT',
            'DYNASTY_TRUST',
            'CHARITABLE_REMAINDER_TRUST',
            'CHARITABLE_LEAD_TRUST',
            'SPECIAL_NEEDS_TRUST',
            'DOMESTIC_ASSET_PROTECTION_TRUST',
            'TESTAMENTARY_TRUST',
            'QUALIFIED_DISABILITY_TRUST'
          )
        )
        OR
        (
          ${table.jurisdiction} = 'CA'
          AND ${table.trustType} IN (
            'FAMILY_TRUST',
            'ALTER_EGO_TRUST',
            'JOINT_PARTNER_TRUST',
            'SPOUSAL_TRUST',
            'HENSON_TRUST',
            'GRADUATED_RATE_ESTATE',
            'BARE_TRUST',
            'CHARITABLE_REMAINDER_TRUST',
            'TESTAMENTARY_TRUST',
            'QUALIFIED_DISABILITY_TRUST'
          )
        )
      )`,
    ),
    index("trust_trustee_contact_id_idx").on(table.trusteeContactId),
    index("trust_trustee_company_id_idx").on(table.trusteeCompanyId),
    index("trust_trustee_trust_id_idx").on(table.trusteeTrustId),
  ],
)
